import { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, Avatar, Button } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Loading from "../../../../../../components/loading/loading";
import { getUserCertificationsRequest } from "../../../../../../redux/users/slice";
import {
  downloadTemplateFileRequest,
  updateCertificationDetailsRequest,
} from "../../../../../../utils/APICalls";
import FannieMae from "./fannie-mae";
import FreddieMac from "./freddie-mac";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

export default function EditCertificate({
  showDialog,
  closeDialog,
  data,
}: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<any>();
  const [selectedCertificateFile, setSelectedCertificateFile] = useState<any>();
  const [uploadedDeclaration, setUploadedDeclaration] = useState<any>(
    data?.declarationFileName || ""
  );
  const [uploadedCertificate, setUploadedCertificate] = useState<any>(
    data?.certificateFileName || ""
  );
  const [deleteCertificate, setDeleteCertificate] = useState(false);
  const [deleteDeclaration, setDeleteDeclaration] = useState(false);
  const [methodCertificateMap, setMethodCertificateMap] = useState<
    Record<string, any>
  >({});
  const [hasChanges, setHasChanges] = useState(false);

  const { certificationMethods } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const isCertificateFileValid = (file: any) => {
    if (file?.size === 0) {
      formik.setFieldError("certificate", "Empty files are not allowed.");
      return false;
    } else if (file?.size > 10 * 1024 * 1024) {
      formik.setFieldError("certificate", "File size exceeds 10MB limit.");
      return false;
    }
    return true;
  };

  const isDeclarationFileValid = (file: any) => {
    if (file?.size === 0) {
      formik.setFieldError("declaration", "Empty files are not allowed.");
      return false;
    } else if (file?.size > 10 * 1024 * 1024) {
      formik.setFieldError("declaration", "File size exceeds 10MB limit.");
      return false;
    }
    return true;
  };

  const handleCertificateFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      formik.setFieldValue("certificate", null);
      formik.setFieldError("certificate", undefined);
      setSelectedCertificateFile(null);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      formik.setFieldError(
        "certificate",
        "Unsupported file type. Only PDF, JPG, or PNG files are allowed."
      );
      formik.setFieldValue("certificate", file);
      setSelectedCertificateFile(file);
      return;
    }

    if (!isCertificateFileValid(file)) {
      formik.setFieldValue("certificate", file);
      setSelectedCertificateFile(file);
      return;
    }

    // If a new file is selected, clear the uploaded certificate and set delete flag
    if (data?.certificateFileName) {
      setUploadedCertificate("");
      setDeleteCertificate(true);
    }

    setSelectedCertificateFile(file);
    formik.setFieldValue("certificate", file);
    formik.setFieldError("certificate", undefined);

    // Persist certificate per certification method
    const currentMethod = formik.values.certificationMethod;
    if (currentMethod) {
      setMethodCertificateMap((prev) => ({ ...prev, [currentMethod]: file }));
    }
  };

  const handleDeclarationFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);

    if (!file) {
      formik.setFieldValue("declaration", null);
      formik.setFieldError("declaration", undefined);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      formik.setFieldError(
        "declaration",
        "Unsupported file type. Only PDF, JPG, or PNG files are allowed."
      );
      formik.setFieldValue("declaration", file);
      return;
    }

    if (!isDeclarationFileValid(file)) {
      formik.setFieldValue("declaration", file);
      return;
    }

    formik.setFieldValue("declaration", file);
    formik.setFieldError("declaration", undefined);
  };

  const handleCertificateFileDelete = () => {
    setSelectedCertificateFile(null);
    formik.setFieldValue("certificate", "");
    formik.setFieldError("certificate", undefined);

    if (data?.certificateFileName) {
      setUploadedCertificate("");
      setDeleteCertificate(true);
    }
  };

  const handleDeclarationFileDelete = () => {
    setSelectedFile(null);
    formik.setFieldValue("declaration", "");
    formik.setFieldError("declaration", undefined);

    if (data?.declarationFileName) {
      setDeleteDeclaration(true);
    }
  };

  const ValidationSchema = yup
    .object({
      // Fannie Mae fields
      certificationMethod: yup.string().when("certificationType", {
        is: "FannieMae",
        then: (schema) =>
          schema.required("Please select a certification method"),
        otherwise: (schema) => schema.optional(),
      }),
      certificateNumber: yup.string().when("certificationMethod", {
        is: "HUDREAC",
        then: (schema) =>
          schema.required("Certificate number is required for HUDREAC"),
        otherwise: (schema) => schema.optional(),
      }),
      certificate: yup
        .mixed()
        .required("This field can't be empty")
        .test(
          "file-type",
          "Only PDF, JPG, or PNG files are allowed.",
          function (value) {
            if (!value || !(value instanceof File)) return true;
            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/jpg",
              "image/png",
            ];
            return allowedTypes.includes(value.type);
          }
        )
        .test("file-size", "File size exceeds 10MB limit.", function (value) {
          if (!value || !(value instanceof File)) return true;
          return value.size <= 10 * 1024 * 1024;
        })
        .when("certificationMethod", {
          is: (val: string) => val && val !== "HUDREAC",
          then: (schema) =>
            schema.test(
              "certificate-required",
              "Certificate file is required for this certification method",
              function (value) {
                return (
                  (value && value instanceof File) ||
                  this.parent.existingCertificate
                );
              }
            ),
          otherwise: (schema) => schema.optional(),
        }),
      declaration: yup
        .mixed()
        .nullable()
        .test(
          "file-type",
          "Only PDF, JPG, or PNG files are allowed.",
          function (value) {
            if (!value || !(value instanceof File)) return true;
            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/jpg",
              "image/png",
            ];
            return allowedTypes.includes(value.type);
          }
        )
        .test("file-size", "File size exceeds 10MB limit.", function (value) {
          if (!value || !(value instanceof File)) return true;
          return value.size <= 10 * 1024 * 1024;
        })
        .when(
          [
            "certificationMethod",
            "isFreddieMacExemptInspector",
            "isCertifiedInspector",
          ],
          {
            is: (
              certificationMethod: string,
              isFreddieMacExemptInspector: boolean,
              isCertifiedInspector: boolean
            ) =>
              !certificationMethod &&
              !isFreddieMacExemptInspector &&
              !isCertifiedInspector,
            then: (schema) =>
              schema.test(
                "declaration-required",
                "Declaration document is required if no certification method is selected",
                function (value) {
                  return (
                    (value && value instanceof File) ||
                    this.parent.existingDeclaration
                  );
                }
              ),
            otherwise: (schema) => schema.optional(),
          }
        ),
      // Freddie Mac fields
      isFreddieMacExemptInspector: yup.boolean(),
      otherCertificationMethod: yup.string().when("certificationMethod", {
        is: "Other",
        then: (schema) => schema.required("Please enter equivalent program"),
        otherwise: (schema) => schema.optional(),
      }),
    })
    .test(
      "freddie-mac-requirements",
      "Please select at least one option",
      function (value: any) {
        if (value.certificationType === "FreddieMac") {
          return (
            value.isFreddieMacExemptInspector || value.isCertifiedInspector
          );
        }
        return true;
      }
    )
    .test(
      "freddie-mac-certification-required",
      "Certification method is required for Certified Inspector",
      function (value: any) {
        if (
          value.certificationType === "FreddieMac" &&
          value.isCertifiedInspector
        ) {
          return value.certificationMethod && value.certificationMethod !== "";
        }
        return true;
      }
    );

  const initialValues = useMemo(
    () => ({
      certificationType:
        data?.certificationType === "Fannie Mae" ? "FannieMae" : "FreddieMac",
      certificationMethod: data?.certificationMethod || "",
      certificateNumber: data?.certificationMetadata?.certificationNumber || "",
      certificate: data?.certificateFileName || "",
      declaration: data?.declarationFileName || "",
      existingCertificate: data?.certificateFileName || "",
      existingDeclaration: data?.declarationFileName || "",
      // Freddie Mac specific fields
      isFreddieMacExemptInspector: data?.isFreddieMacExemptInspector || false,
      isCertifiedInspector:
        (data?.certificationType === "Freddie Mac" &&
          data?.certificationMethod) ||
        false,
      otherCertificationMethod:
        data?.certificationMetadata?.certificationName || "",
    }),
    [
      data?.certificationType,
      data?.certificationMethod,
      data?.certificationMetadata?.certificationNumber,
      data?.certificateFileName,
      data?.declarationFileName,
      data?.isFreddieMacExemptInspector,
      data?.certificationMetadata?.certificationName,
    ]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      // Set all fields as touched to show validation errors
      formik.setTouched({
        certificationType: true,
        certificationMethod: true,
        certificateNumber: true,
        certificate: true,
        declaration: true,
        existingCertificate: true,
        existingDeclaration: true,
        isFreddieMacExemptInspector: true,
        isCertifiedInspector: true,
        otherCertificationMethod: true,
      });

      const isValid = await formik.validateForm();
      if (!isValid) {
        return;
      }
      updateCertificationDetails(values);
    },
    enableReinitialize: true,
  });

  const updateCertificationDetails = async (values: any) => {
    const formData = new FormData();
    formData.append(
      "certificationType",
      data?.certificationType === "Fannie Mae" ? "FannieMae" : "FreddieMac"
    );

    if (values.certificationType === "FannieMae") {
      if (values.certificationMethod === "HUDREAC") {
        formData.append(
          "certificationMethod",
          values.certificationMethod || ""
        );
        formData.append("certificateNumber", values.certificateNumber || "");
      } else if (values.certificate && values.certificate instanceof File) {
        formData.append(
          "certificationMethod",
          values.certificationMethod || ""
        );
      }
    } else if (values.certificationType === "FreddieMac") {
      formData.append(
        "isFreddieMacExemptInspector",
        values.isFreddieMacExemptInspector ? "true" : "false"
      );

      // Handle isCertifiedInspector changes
      const originalCertifiedStatus =
        (data?.certificationType === "Freddie Mac" &&
          data?.certificationMethod) ||
        false;
      if (values.isCertifiedInspector !== originalCertifiedStatus) {
        if (values.isCertifiedInspector) {
          // Inspector is now certified - send certification method if available
          if (values.certificationMethod && values.certificationMethod !== "") {
            formData.append("certificationMethod", values.certificationMethod);
            if (values.certificationMethod === "Other") {
              formData.append(
                "otherCertificationMethod",
                values.otherCertificationMethod || ""
              );
            }
          }
        } else {
          // Inspector is no longer certified - delete certificate
          formData.append("deleteCertificate", "true");
        }
      } else if (values.isCertifiedInspector && values.certificationMethod) {
        // Inspector is still certified but certification method might have changed
        const originalMethod = data?.certificationMethod || "";
        const originalOtherMethod =
          data?.certificationMetadata?.certificationName || "";
        const isUploadingNewCertificate =
          values.certificate && values.certificate instanceof File;

        if (
          values.certificationMethod !== originalMethod ||
          isUploadingNewCertificate ||
          (values.certificationMethod === "Other" &&
            values.otherCertificationMethod !== originalOtherMethod)
        ) {
          formData.append("certificationMethod", values.certificationMethod);
          if (values.certificationMethod === "Other") {
            formData.append(
              "otherCertificationMethod",
              values.otherCertificationMethod || ""
            );
          }
        }
      }
    }

    // Only send declaration if it's a new file
    if (values.declaration && values.declaration instanceof File) {
      formData.append("declaration", values.declaration);
    }

    // Only send certificate if it's a new file
    if (values.certificate && values.certificate instanceof File) {
      formData.append("certificate", values.certificate);
    }

    // Handle existing file deletions
    if (data?.certificateFileName) {
      formData.append(
        "deleteCertificate",
        deleteCertificate ? "true" : "false"
      );
    }
    if (data?.declarationFileName) {
      formData.append(
        "deleteDeclaration",
        deleteDeclaration ? "true" : "false"
      );
    }

    setShowLoading(true);
    const response = await updateCertificationDetailsRequest(
      data?.id,
      formData
    );
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getUserCertificationsRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  useEffect(
    () => {
      if (
        data?.certificationType === "Freddie Mac" &&
        !data?.certificationMethod
      ) {
        const freddieMacMethods = certificationMethods.filter(
          (type: any) => type.certificationType === "FreddieMac"
        );
        if (freddieMacMethods.length > 0) {
          formik.setFieldValue(
            "certificationMethod",
            freddieMacMethods[0].certificationMethod
          );
        }
      }
    },
    // eslint-disable-next-line
    [certificationMethods, data?.certificationType, data?.certificationMethod]
  ); // eslint-disable-line

  // Check for changes
  useEffect(() => {
    const checkForChanges = () => {
      const currentValues = formik.values;
      const hasFileChanges =
        selectedFile ||
        selectedCertificateFile ||
        deleteCertificate ||
        deleteDeclaration;

      const hasFormChanges =
        currentValues.certificationMethod !==
          initialValues.certificationMethod ||
        currentValues.certificateNumber !== initialValues.certificateNumber ||
        currentValues.isFreddieMacExemptInspector !==
          initialValues.isFreddieMacExemptInspector ||
        currentValues.isCertifiedInspector !==
          initialValues.isCertifiedInspector ||
        currentValues.otherCertificationMethod !==
          initialValues.otherCertificationMethod;

      setHasChanges(hasFileChanges || hasFormChanges);
    };

    checkForChanges();
  }, [
    formik.values,
    selectedFile,
    selectedCertificateFile,
    deleteCertificate,
    deleteDeclaration,
    initialValues,
  ]);

  const hideDialog = () => {
    formik.resetForm();
    setDeleteCertificate(false);
    setDeleteDeclaration(false);
    setSelectedCertificateFile(null);
    setSelectedFile(null);
    setUploadedCertificate(data?.certificateFileName || "");
    setUploadedDeclaration(data?.declarationFileName || "");
    setMethodCertificateMap({});
    closeDialog();
  };

  // Initialize per-method certificate map from existing data
  useEffect(
    () => {
      if (data?.certificationMethod) {
        setMethodCertificateMap((prev) => ({
          ...prev,
          [data.certificationMethod as string]: data?.certificateFileName || "",
        }));
      }
    },
    // eslint-disable-next-line
    [data?.certificationMethod, data?.certificateFileName]
  ); // eslint-disable-line

  const handleCertificationMethodChange = (newMethod: string) => {
    const previousMethod = formik.values.certificationMethod as string;

    // Persist current method's certificate into the map
    if (previousMethod) {
      const valueToStore =
        selectedCertificateFile instanceof File
          ? selectedCertificateFile
          : uploadedCertificate || formik.values.certificate || "";
      setMethodCertificateMap((prev) => ({
        ...prev,
        [previousMethod]: valueToStore,
      }));
    }

    // Update method in formik
    formik.setFieldValue("certificationMethod", newMethod);

    // Restore certificate for the new method if present
    const stored = (methodCertificateMap as any)[newMethod];
    if (stored) {
      if (stored instanceof File) {
        setSelectedCertificateFile(stored);
        setUploadedCertificate("");
        formik.setFieldValue("certificate", stored);
      } else {
        setSelectedCertificateFile(null);
        setUploadedCertificate(stored as string);
        formik.setFieldValue("certificate", stored);
      }
      setDeleteCertificate(false);
    } else {
      setSelectedCertificateFile(null);
      setUploadedCertificate("");
      formik.setFieldValue("certificate", "");
      formik.setFieldError("certificate", undefined);
    }

    // Trigger validation for the certificate field when method changes
    if (newMethod && newMethod !== "HUDREAC") {
      // For non-HUDREAC methods, validate certificate requirement
      setTimeout(() => {
        formik.validateField("certificate");
      }, 0);
    }

    if (newMethod !== "Other") {
      formik.setFieldValue("otherCertificationMethod", "");
    }
  };

  const onDownloadClick = async () => {
    setShowLoading(true);
    try {
      const templateType =
        formik.values.certificationType === "FannieMae"
          ? "FannieMaeCertificationDeclaration"
          : "FreddieMacCertificationDeclaration";
      const response = await downloadTemplateFileRequest(templateType);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "Certificate_Declaration";
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <DialogContainer
      title={`Users - Certifications - ${data?.certificationType} Certifications`}
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
      buttonText="Save Changes/Upload Files"
      saveButtonDisabled={!hasChanges}
    >
      <Box>
        <Typography>
          In order to perform {data?.certificationType} Inspections, the
          inspector must:
        </Typography>
        <Box
          sx={{
            margin: "10px 0",
            backgroundColor: "#FAFAFA",
            borderRadius: "4px",
            padding: "16px",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                bgcolor: "#212121",
                width: 20,
                height: 20,
                fontSize: "12px",
              }}
            >
              1
            </Avatar>
            <Typography variant="body1" sx={{ fontSize: "16px" }}>
              {`Meet ${
                formik.values.certificationType === "FannieMae"
                  ? ""
                  : "at least "
              }1 of the following`}
            </Typography>
          </Box>
          {formik.values.certificationType === "FannieMae" ? (
            <FannieMae
              formik={formik}
              certificationMethods={certificationMethods}
              uploadedCertificate={uploadedCertificate}
              handleCertificateFileChange={handleCertificateFileChange}
              handleCertificateFileDelete={handleCertificateFileDelete}
              setUploadedCertificate={setUploadedCertificate}
              setDeleteCertificate={setDeleteCertificate}
              selectedCertificateFile={selectedCertificateFile}
              onCertificationMethodChange={handleCertificationMethodChange}
              data={data}
            />
          ) : (
            <FreddieMac
              formik={formik}
              certificationMethods={certificationMethods}
              uploadedCertificate={uploadedCertificate}
              handleCertificateFileChange={handleCertificateFileChange}
              handleCertificateFileDelete={handleCertificateFileDelete}
              setUploadedCertificate={setUploadedCertificate}
              setDeleteCertificate={setDeleteCertificate}
              selectedCertificateFile={selectedCertificateFile}
              onCertificationMethodChange={handleCertificationMethodChange}
              data={data}
            />
          )}
        </Box>
        <Box
          sx={{
            margin: "10px 0",
            backgroundColor: "#FAFAFA",
            borderRadius: "4px",
            padding: "16px",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{
                  bgcolor: "#212121",
                  width: 20,
                  height: 20,
                  fontSize: "12px",
                }}
              >
                2
              </Avatar>
              <Typography variant="body1" sx={{ fontSize: "16px" }}>
                Download, sign, and upload the declaration document{" "}
              </Typography>
            </Box>
            <Button
              sx={{ fontWeight: "510" }}
              color="primary"
              startIcon={<DownloadOutlinedIcon />}
              onClick={onDownloadClick}
            >
              Download
            </Button>
          </Box>
          <Box sx={{ pl: "36px" }}>
            {!uploadedDeclaration &&
              !formik.values.declaration &&
              !selectedFile && (
                <Box
                  sx={{
                    height: 80,
                    width: "100%",
                    border: formik.errors.declaration
                      ? "1px solid red"
                      : "1px dashed rgba(0, 0, 0, 0.12)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    mt: 2,
                  }}
                >
                  <UploadFileIcon sx={{ ml: 5 }} color="primary" />
                  <Box ml={2}>
                    <Box sx={{ display: "flex" }}>
                      <Typography
                        color="primary"
                        sx={{ textDecoration: "underline" }}
                      >
                        Click to upload
                      </Typography>
                      <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
                    </Box>
                    <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                      PDF, JPG or PNG (max. 10MB)
                    </Typography>
                  </Box>
                  <input
                    style={{
                      display: "block",
                      height: 80,
                      width: "100%",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      opacity: 0,
                      cursor: "pointer",
                    }}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
                    onChange={handleDeclarationFileChange}
                    name="declaration"
                    onClick={(event) => {
                      (event.target as HTMLInputElement).value = "";
                    }}
                  />
                </Box>
              )}
            {(selectedFile ||
              (formik.values.declaration &&
                formik.values.declaration !== data?.declarationFileName)) && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: formik.errors.declaration
                    ? "1.5px solid red"
                    : "1.5px solid green",
                  p: 0.8,
                  borderRadius: 1,
                  backgroundColor: formik.errors.declaration
                    ? "#FFF5F5"
                    : "#F7FAF7",
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <AttachFileOutlinedIcon color="primary" />
                  <Box sx={{ ml: 1.5 }}>
                    <Typography>
                      {selectedFile?.name ||
                        (formik.values.declaration instanceof File
                          ? formik.values.declaration.name
                          : formik.values.declaration) ||
                        "No file selected"}
                    </Typography>
                    <Typography>
                      {" "}
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} kb`
                        : formik.values.declaration instanceof File
                        ? `${(formik.values.declaration.size / 1024).toFixed(
                            2
                          )} kb`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton
                    aria-label="delete"
                    onClick={handleDeclarationFileDelete}
                  >
                    <RemoveCircleOutlineIcon color="error" />
                  </IconButton>
                </Box>
              </Box>
            )}
            {uploadedDeclaration &&
              !selectedFile &&
              formik.values.declaration === data?.declarationFileName && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1.5px solid green",
                    p: 0.8,
                    borderRadius: 1,
                    backgroundColor: "#F7FAF7",
                  }}
                >
                  <Box display={"flex"} alignItems={"center"}>
                    <AttachFileOutlinedIcon color="primary" />
                    <Box sx={{ ml: 1.5 }}>
                      <Typography>{uploadedDeclaration}</Typography>
                      <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                        Complete
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        setUploadedDeclaration("");
                        formik.setFieldValue("declaration", "");
                        if (data?.declarationFileName) {
                          setDeleteDeclaration(true);
                        }
                      }}
                    >
                      <DeleteOutlinedIcon color="error" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            {formik.errors.declaration && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                {formik.errors.declaration as string}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {showLoading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
